import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components-new/dialog';
import { Button } from '@/components-new/button';
import { Heading } from '@/components-new/heading';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@/components-new/table';
import { notifySuccess } from '@/lib/notification/notifications';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useImportDraftCoverageSet } from '../api/queries';
import { zodResolver } from '@hookform/resolvers/zod';
import { importDraftCoverageSetInputSchema } from '../api/api';
import { useNavigate } from '@/router';
import { ErrorMessage, Field } from '@/components-new/fieldset';
import { FileUpload, FileUploadContainer } from '@/components-new/file-upload';

export type ImportDraftCoverageFormInput = {
  coverageFile: File;
}

export const ImportDraftCoverageDialog = ({ isOpen, onClose } : { isOpen: boolean, onClose: () => void }) => {
  const { control, handleSubmit } = useForm<ImportDraftCoverageFormInput>({
    resolver: zodResolver(importDraftCoverageSetInputSchema),
    defaultValues: {
      coverageFile: null as any,
    }
  });

  const { mutate: importCoverage, isPending: isImporting } = useImportDraftCoverageSet();
  const navigate = useNavigate();

  const resetForm = () => {
    control._reset();
    onClose();
  }

  const submitImport: SubmitHandler<ImportDraftCoverageFormInput> = async (data) => {
    importCoverage(
      data,
      {
        onSuccess: (id) => {
          notifySuccess({
            title: "Coverage Draft(s) Created",
            //@ts-ignore
            message: `${data.response.data} records were imported.`
          });

          navigate('/drug-coverage/drafts');
        }
      }
    );
    resetForm();
    notifySuccess({ title: "Draft coverage import started..." });
  };

  return (
    <Dialog size="5xl" open={isOpen} onClose={resetForm}>

      <form className={''} onSubmit={handleSubmit(submitImport)}>
      <DialogTitle>Import State Coverage</DialogTitle>
      <DialogDescription>Create a new draft by importing a CSV file.</DialogDescription>
      <DialogBody>
        <ImportFileColumnRequirements />
        <div className={'col-span-10 pt-8'}>
          <Controller
            control={control}
            name="coverageFile"
            defaultValue={undefined}
            render={({ field: { value, onChange, name }, fieldState: { error } }) => (
              <Field>
                <FileUploadContainer>
                  <FileUpload name={name} accept=".csv" fileName={value?.name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      if (event.target.files != null && event.target.files.length > 0) {
                        onChange(event.target.files[0]);
                      }
                    }}
                  />
                </FileUploadContainer>
                {error?.message && <ErrorMessage>{error?.message}</ErrorMessage>}
              </Field>
            )}
          />
        </div>

      </DialogBody>
      <DialogActions>
        <Button outline onClick={resetForm}>Cancel</Button>

        {isImporting ?
          <Button color="primary" disabled>Importing...</Button> :
          <Button type="submit" color="primary" >Import</Button>
        }


      </DialogActions>
      </form>
    </Dialog>
  );
}

const ImportFileColumnRequirements = () => {
  return(
    <>
    <Heading level={3} >Import File Column Requirements</Heading>
    <Table dense layoutMode="semantic" className={''}>
      <TableHead>
        <TableRow>
          <TableCell>Column Header</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>Notes</TableCell>
          <TableCell className={''}>Example</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell className={'font-bold'}>Drug</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>Name must match a drug in Capsule.</TableCell>
          <TableCell>Vigafyde</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>State</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>Full state name.</TableCell>
          <TableCell>Florida</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>PDL Status Date</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>Valid date.</TableCell>
          <TableCell>7/1/25</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>PDL Status</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>"Preferred", "Non-Preferred", "Non-PDL", "No State PDL", "Preferred (Not Listed)", "Non-Preferred (Not Listed)", or "Pending"</TableCell>
          <TableCell>Non-Preferred</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>Coverage Tags</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>Semi-colon separated. Must match tags in Capsule.</TableCell>
          <TableCell>oral solution; Epilepsy</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>PA Type</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>"General PDL", "General Non-PDL", "Class-Specific", "Drug-Specific", or "Oncology"</TableCell>
          <TableCell>Class-Specific</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>Step Therapy</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>Number with optional plus sign.</TableCell>
          <TableCell>1+</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>PA Criteria Link</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>URL</TableCell>
          <TableCell className={'max-w-[200px] overflow-auto'}>https://ahca.myflorida.com/content/download/23641/file/FL_PA_Pharm_Misc_Form.pdf</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>State PA Criteria Link same as State PDL Link?</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>Yes or No. If Yes, PA Criteria Link will be overridden with the value in Capsule.</TableCell>
          <TableCell>No</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>Has Auto PA?</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>Yes or No</TableCell>
          <TableCell>No</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>PA Criteria Text</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>Rich Text Formatted</TableCell>
          <TableCell>
            1 - History of failure, contraindication, or intolerance to a trial of at least two of the following preferred agents:

            • Eszopiclone (Generic Lunesta)

            • Zolpidem/Zolpidem ER (Generic Ambien/Ambien CR)

            • Temazepam 15/30mg capsules (Generic Restoril) AND

            2 - For generic ramelteon requests ONLY, patient must have tried and failed Brand Rozerem
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={'font-bold'}>Notes</TableCell>
          <TableCell>Optional</TableCell>
          <TableCell>Rich Text Formatted</TableCell>
          <TableCell>
            PDL indicates * Clinical criteria may apply to this therapeutic class, but no form exists. Applying General PDL criteria.
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
    </>
  );
}
